import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthPayload, getUserEmail } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  Approved: 'Approved',
  Rejected: 'Rejected',
  Pending: 'Pending',
  'Under Review': 'Under Review',
  Denied: 'Denied',
};

const STATUS_VARIANTS = {
  Approved: 'success',
  Rejected: 'danger',
  Pending: 'warning',
  'Under Review': 'info',
  Denied: 'danger',
};

const DEFAULT_OPEN_SECTIONS = {
  general: true,
  contact: false,
  project: false,
  budget: false,
};

const DEFAULT_RISK_OPEN_SECTIONS = {
  fraudAlerts: true,
  businessIdentity: false,
  financialRedFlags: false,
  courtRecords: false,
  negativeNews: false,
  relatedEntities: false,
  otherNames: false,
  incorporationRecords: false,
  osintIntelligence: false,
  revenueTrend: false,
  corporateActivity: false,
  relationshipMapping: false,
  activityTimeline: false,
  dataSources: false,
};

const WORKSPACE_TABS = [
  { id: 'details', label: 'Application Details' },
  { id: 'prescreening', label: 'AI Prescreening' },
  { id: 'risk', label: 'AI Risk Assessment' },
  { id: 'review', label: 'Reviewer Notes' },
  { id: 'all-reviews', label: 'All Reviews' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'activity', label: 'Activity Log' },
];

const FORMAL_REVIEW_STATUS_OPTIONS = ['Draft', 'Completed'];
const FORMAL_RECOMMENDATION_OPTIONS = ['Advance', 'Hold', 'Decline', 'Needs Follow-up'];
const PRESCREEN_SUMMARY_HIDDEN_PREFIXES = [
  'REQUIRED_FILE_COUNT:',
  'REQUIRED_SECTIONS_PRESENT:',
  'REQUIRED_CONTENT_SECTIONS:',
  'STATE_ALIGNMENT:',
];

function formatCheckLabel(value) {
  return String(value || '')
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ')
    .trim();
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function formatMoney(value) {
  if (value == null || value === '') return '';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return `$${number.toLocaleString()}`;
}

function formatBoolean(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function firstDefined(...values) {
  return values.find((value) => value != null && value !== '') || '';
}

function formatConfidence(value) {
  if (value == null || Number.isNaN(Number(value))) return '1.00';
  return Number(value).toFixed(2);
}

function formatNarrativePoints(values) {
  const items = (values || []).filter(Boolean);
  return items.length ? items.join('; ') : '—';
}

function normalizeDocumentLookupName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function buildFormalReviewCriteria(criteria = [], reviewCriteria = []) {
  const savedByTitle = new Map((reviewCriteria || []).map((criterion) => [criterion.title, criterion]));
  return (criteria || []).map((criterion) => {
    const saved = savedByTitle.get(criterion.title) || {};
    return {
      title: criterion.title || '',
      description: criterion.description || '',
      maxScore: Number(criterion.maxScore || 0),
      awardedScore: saved.awardedScore != null && saved.awardedScore !== '' ? String(saved.awardedScore) : '',
      notes: saved.notes || '',
    };
  });
}

function getAIRunSourceDetail(run) {
  if (!run) return '';
  if (run.aiUsed) return 'Model-generated output';
  return run.fallbackReason || 'AI provider unavailable, so the saved fallback logic was used.';
}

function DetailField({ label, value, wide = false }) {
  return (
    <label className={`record-field ${wide ? 'record-field-wide' : ''}`}>
      <span className="record-field-label">{label}</span>
      <div className="record-field-value">{value || '—'}</div>
    </label>
  );
}

function StatusChip({ status }) {
  if (!status) return <span className="status-badge neutral">Unknown</span>;
  const normalized = String(status).toLowerCase().replace(/\s+/g, '-');
  return <span className={`status-badge workspace-status-chip ${normalized}`}>{status}</span>;
}

function RiskSeverityBadge({ level }) {
  const normalized = String(level || 'low').toLowerCase();
  return <span className={`risk-severity-badge ${normalized}`}>{String(level || 'LOW').toUpperCase()}</span>;
}

async function readApiPayload(response) {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';

  if (!text) return null;
  if (contentType.includes('application/json')) {
    return JSON.parse(text);
  }

  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return JSON.parse(trimmed);
  }

  throw new Error('Risk service returned a non-JSON response.');
}

function buildLocalRiskDashboardFallback(application, existingNotes = '') {
  return {
    grantContext: {
      grantId: application?._id || application?.id || '',
      grantNumber: application?.applicationNumber || '',
      organizationName: 'DOWNTOWN DETROIT PARTNERSHIP INC',
      legalName: 'DOWNTOWN DETROIT PARTNERSHIP INC',
      taxId: '38-3436456',
      uei: 'L1BKG5TDD4K7',
      status: application?.status || '',
    },
    summary: {
      score: 41,
      level: 'MEDIUM',
      alertsCount: 6,
      highAlerts: 2,
      courtCount: 5,
      newsCount: 3,
      isDemoData: true,
      prescreeningRequired: false,
    },
    organization: {
      score: 41,
      level: 'MEDIUM',
      alertsCount: 6,
      highAlerts: 2,
      courtCount: 5,
      newsCount: 3,
      isDemoData: true,
      aiSummary: 'Pinnacle Consulting Group LLC presents a MEDIUM overall risk profile based on analysis of 6 risk signals across identity verification, financial history, legal records, and open-source intelligence.',
      breakdown: {
        highRisk: { totalScore: 28, alertCount: 2 },
        mediumRisk: { totalScore: 12, alertCount: 3 },
        lowRisk: { totalScore: 1, alertCount: 1 },
        osintScore: 14,
      },
      alerts: [
        { id: 'LN-HAS-BANKRUPTCY', severity: 'HIGH', title: 'A bankruptcy has been recorded for this business. Chapter 11 filing (Case #19-00542-DC) filed 07/22/2019, dismissed 03/10/2020 in U.S. Bankruptcy Court, District of Columbia.' },
        { id: 'LN-HAS-LIEN-JUDGMENT', severity: 'HIGH', title: 'A lien or judgment has been recorded for this business. Federal Tax Lien of $45,000 filed 04/03/2022 by IRS. Breach of Contract judgment of $125,000 filed 01/18/2023 in D.C. Superior Court.' },
        { id: 'LN-HAS-UCC', severity: 'MEDIUM', title: 'One or more UCC filings are associated with this business. Filing 2021-DC-0048372 dated 08/14/2021, secured by Capital One Equipment Finance.' },
        { id: 'LEGAL-DEF-MATCH', severity: 'MEDIUM', title: 'The business is listed as a defendant in a legal case. TechForward Inc v. Pinnacle Consulting Group LLC (2022-CV-01284, Trade Secret Misappropriation, terminated 11/15/2023).' },
        { id: 'OC-SECRECY-STATE', severity: 'MEDIUM', title: 'Incorporated in Delaware - known for minimal disclosure requirements. Foreign qualification filing DE-5284917 dated 06/22/2012.' },
        { id: 'ADDR-CMRA', severity: 'LOW', title: 'The address is a mail drop at a commercial mail receiving agency (e.g., UPS Store). Address: 1847 K Street NW, Suite 400, Washington, DC 20006.' },
      ],
      relatedPeople: [
        { fullName: 'David R. Nakamura', role: 'CEO / Managing Member' },
        { fullName: 'Rebecca Torres', role: 'CFO' },
        { fullName: 'Michael A. Whitfield', role: 'General Counsel' },
        { fullName: 'Priya Ramirez', role: 'VP Operations' },
      ],
      relatedBusinesses: [
        { businessName: 'Pinnacle Holdings International Inc', taxId: '99-0000002' },
        { businessName: 'Apex Data Solutions LLC', taxId: '99-0000003' },
      ],
      incorporationRecords: [
        { filingNumber: 'DC-2012-03847', type: 'Limited Liability Company', state: 'DC', foreignState: '—', status: 'ACTIVE', filed: '03/15/2012', registeredAgent: 'National Registered Agents Inc' },
        { filingNumber: 'DE-5284917', type: 'Foreign Limited Liability Company', state: 'DC', foreignState: 'DE', status: 'ACTIVE', filed: '06/22/2012', registeredAgent: 'Corporation Trust Company' },
      ],
      financeSnapshots: [
        { year: '2024', annualRevenue: '$4,200,000' },
        { year: '2023', annualRevenue: '$3,800,000' },
        { year: '2022', annualRevenue: '$3,100,000' },
      ],
      financialRedFlags: [
        { type: 'BANKRUPTCY', filed: '07/22/2019', detail: 'Chapter 11 – Dismissed (U.S. Bankruptcy Court, District of Columbia)', severity: 'high' },
        { type: 'LIEN', amount: '$45,000', filed: '04/03/2022', detail: 'Federal Tax Lien – Internal Revenue Service', severity: 'medium' },
        { type: 'JUDGMENT', amount: '$125,000', filed: '01/18/2023', detail: 'Breach of Contract – D.C. Superior Court', severity: 'medium' },
        { type: 'UCC', filed: '08/14/2021', detail: 'Filing #2021-DC-0048372 – Capital One Equipment Finance', severity: 'low' },
      ],
      courtCases: [
        { source: 'FEDERAL', caseNumber: '1:21-cr-00198', caseName: 'United States v. Pinnacle Consulting Group LLC', court: 'U.S. District Court for the District of Columbia', role: '—', type: 'Criminal', filed: '08/03/2021', status: 'TERMINATED' },
        { source: 'FEDERAL', caseNumber: '1:24-cv-00412', caseName: 'Pinnacle Consulting Group LLC v. Department of Defense', court: 'U.S. District Court for the District of Columbia', role: '—', type: 'Civil', filed: '02/28/2024', status: 'ACTIVE' },
        { source: 'STATE', caseNumber: '2021-CR-00387', caseName: 'United States v. Nakamura et al.', court: 'D.C. Superior Court', role: 'Defendant', type: 'Criminal', filed: '09/14/2021', status: 'TERMINATED' },
        { source: 'STATE', caseNumber: '2024-CV-04821', caseName: 'Pinnacle Consulting Group LLC v. Meridian Government Services LLC', court: 'D.C. Superior Court', role: 'Plaintiff', type: 'Civil', filed: '06/12/2024', status: 'ACTIVE' },
        { source: 'STATE', caseNumber: '2022-CV-01284', caseName: 'TechForward Inc v. Pinnacle Consulting Group LLC', court: 'Virginia Circuit Court, Arlington County', role: 'Defendant', type: 'Civil', filed: '03/08/2022', status: 'TERMINATED' },
      ],
      federalCourtCases: [
        { source: 'FEDERAL', caseNumber: '1:21-cr-00198', caseName: 'United States v. Pinnacle Consulting Group LLC', court: 'U.S. District Court for the District of Columbia', role: '—', type: 'Criminal', filed: '08/03/2021', status: 'TERMINATED' },
        { source: 'FEDERAL', caseNumber: '1:24-cv-00412', caseName: 'Pinnacle Consulting Group LLC v. Department of Defense', court: 'U.S. District Court for the District of Columbia', role: '—', type: 'Civil', filed: '02/28/2024', status: 'ACTIVE' },
      ],
      negativeNews: [
        { title: 'DC Consulting Firm Settles False Claims Act Case for $1.2M', source: 'Washington Business Journal', date: '2022-05-18', match: 'High', summary: 'Pinnacle Consulting Group LLC agreed to pay $1.2 million to resolve allegations of overbilling on federal IT contracts.', tag: 'Legal/Regulatory' },
        { title: 'Government Contractor Faces New Contract Dispute with DoD', source: 'Federal News Network', date: '2024-03-05', match: 'High', summary: 'Pinnacle Consulting Group has filed suit against the Department of Defense over a terminated contract valued at $4.8 million.', tag: 'Legal/Contract' },
        { title: 'Corporate Restructuring Raises Questions at Pinnacle Consulting', source: 'GovCon Wire', date: '2024-08-12', match: 'Medium', summary: 'Multiple ownership changes at Pinnacle Consulting Group within a two-year period have drawn attention from oversight bodies.', tag: 'Corporate Governance' },
      ],
      otherNames: ['Pinnacle Consulting', 'PCS Advisory Services'],
      osintPeople: [
        { fullName: 'David R. Nakamura', role: 'CEO / Managing Member', source: 'LinkedIn', earliestRecord: '2012-03-15' },
        { fullName: 'Rebecca Torres', role: 'CFO', source: 'SEC EDGAR', earliestRecord: '2015-09-01' },
        { fullName: 'James K. Morrison', role: 'Former Board Advisor', source: 'Bloomberg', earliestRecord: '2018-06-20' },
      ],
      osintBusinesses: [
        { businessName: 'Pinnacle Holdings International Inc', relationship: 'Parent Company', source: 'OpenCorporates' },
        { businessName: 'Meridian Government Services LLC', relationship: 'Subcontractor', source: 'USASpending.gov' },
      ],
      revenueTrend: [
        { year: '2024', annualRevenue: '$4,200,000' },
        { year: '2023', annualRevenue: '$3,800,000' },
        { year: '2022', annualRevenue: '$3,100,000' },
      ],
      corporateActivity: [
        { type: 'restructuring', title: 'Pinnacle Consulting Reorganizes Under New Holding Structure', date: '2024-01-15' },
        { type: 'acquisition', title: 'Pinnacle Consulting Acquires Apex Data Solutions', date: '2023-04-20' },
      ],
      relationshipMapping: {
        relatedEntities: ['DOWNTOWN DETROIT PARTNERSHIP INC'],
      },
      businessIdentity: {
        name: 'DOWNTOWN DETROIT PARTNERSHIP INC',
        ein: '38-3436456',
        uei: 'L1BKG5TDD4K7',
        address: '1000 Woodward, Detroit, MI 48226-3540',
        incorporated: '03/15/2012',
        yearsInBusiness: 14,
        industry: 'Computer Services - Systems Design & Software',
        website: 'www.pinnacleconsultinggroup.com',
        status: 'Active',
      },
    },
    primaryContact: {
      id: `grant_${application?.applicationNumber || application?._id || ''}`,
      fullName: `${application?.firstName || ''} ${application?.lastName || ''}`.trim() || 'Primary Contact',
      email: application?.email || '',
      phone: application?.phone || '',
      role: 'Primary Contact',
    },
    people: [
      {
        contact: {
          id: `grant_${application?.applicationNumber || application?._id || ''}`,
          fullName: `${application?.firstName || ''} ${application?.lastName || ''}`.trim() || 'Primary Contact',
          email: application?.email || '',
          phone: application?.phone || '',
          role: 'Primary Contact',
        },
        result: {
          personId: `grant_${application?.applicationNumber || application?._id || ''}`,
          label: `${application?.firstName || ''} ${application?.lastName || ''}`.trim() || 'Primary Contact',
          level: 'MEDIUM',
        },
      },
    ],
    reviewerNotes: existingNotes,
    latestPrescreeningAt: application?.latestPrescreening?.createdAt || '',
  };
}

function EmptyState({ title, body }) {
  return (
    <div className="workspace-empty-state">
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

function DetailSection({ title, sectionKey, openSections, onToggle, children, defaultOpen = false }) {
  const isOpen = openSections[sectionKey] ?? defaultOpen;

  return (
    <section className="detail-accordion">
      <button
        type="button"
        className={`detail-accordion-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => onToggle(sectionKey)}
      >
        <span className="detail-accordion-icon">{isOpen ? '−' : '+'}</span>
        <span>{title}</span>
      </button>
      {isOpen && <div className="detail-accordion-body">{children}</div>}
    </section>
  );
}

function RiskSection({ title, sectionKey, openSections, onToggle, children, className = '' }) {
  const isOpen = openSections[sectionKey] ?? true;

  return (
    <section className={`workspace-risk-section ${className}`.trim()}>
      <button
        type="button"
        className={`workspace-risk-section-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => onToggle(sectionKey)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <span className="workspace-risk-section-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen ? <div className="workspace-risk-section-body">{children}</div> : null}
    </section>
  );
}

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authPayload = getAuthPayload();
  const userEmail = getUserEmail(authPayload);

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const [openRiskSections, setOpenRiskSections] = useState(DEFAULT_RISK_OPEN_SECTIONS);
  const [expandedCheck, setExpandedCheck] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [riskDashboard, setRiskDashboard] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState('');
  const [selectedRiskPersonId, setSelectedRiskPersonId] = useState('');
  const [showAIAnalysisNotice, setShowAIAnalysisNotice] = useState(false);
  const [formalReviewForm, setFormalReviewForm] = useState({
    score: '',
    recommendation: 'Advance',
    status: 'Draft',
    comments: '',
    criterionReviews: [],
  });

  const currentReview = useMemo(
    () => (application?.formalReviews || []).find((review) => review.reviewerEmail === userEmail) || null,
    [application, userEmail]
  );

  const loadApplicationWorkspace = useCallback(async (applicationId, options = {}) => {
    const { quiet = false } = options;
    const applicationResponse = await fetch(`${API_BASE_URL}/api/applications/${applicationId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!applicationResponse.ok) {
      throw new Error('Failed to load application');
    }

    const applicationBody = await applicationResponse.json();
    setApplication(applicationBody);

    if (applicationBody.latestPrescreening?.checks?.[0]?.checkCode) {
      setExpandedCheck((current) => current || applicationBody.latestPrescreening.checks[0].checkCode);
    }

    if (!quiet) {
      setError('');
    }

    return applicationBody;
  }, [token]);

  const loadRiskDashboard = useCallback(async (applicationId, options = {}) => {
    const { quiet = false } = options;
    setRiskLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/risk/grants/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await readApiPayload(response);

      if (!response.ok) {
        throw new Error(body?.message || 'Unable to load risk and compliance data.');
      }

      setRiskDashboard(body);
      setSelectedRiskPersonId((current) => {
        const availableIds = (body.people || []).map((item) => item.contact?.id).filter(Boolean);
        if (current && availableIds.includes(current)) return current;
        return availableIds[0] || '';
      });
      if (!quiet) {
        setRiskError('');
      }
      return body;
    } catch (loadError) {
      if (!quiet) {
        setRiskError(loadError.message || 'Unable to load risk and compliance data.');
      }
      setRiskDashboard(null);
      return null;
    } finally {
      setRiskLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadApplicationWorkspace(id);
        await loadRiskDashboard(id, { quiet: true });
      } catch {
        setLoadError('Could not load application review workspace.');
      } finally {
        setLoading(false);
      }
    };

    if (token && id) {
      load();
    }
  }, [id, token, loadApplicationWorkspace, loadRiskDashboard]);

  useEffect(() => {
    const baseCriteria = application?.scoringCriteria || [];
    if (!currentReview) {
      setFormalReviewForm({
        score: '',
        recommendation: 'Advance',
        status: 'Draft',
        comments: '',
        criterionReviews: buildFormalReviewCriteria(baseCriteria),
      });
      return;
    }

    setFormalReviewForm({
      score: currentReview.score ?? '',
      recommendation: currentReview.recommendation || 'Advance',
      status: currentReview.status || 'Draft',
      comments: currentReview.comments || '',
      criterionReviews: buildFormalReviewCriteria(baseCriteria, currentReview.criterionReviews || []),
    });
  }, [currentReview, application?.scoringCriteria]);

  const topStatus = firstDefined(application?.finalDecision, application?.status, application?.sourcePayload?.Determination?.IsFunded === false ? 'Unfunded' : '');
  const statusText = STATUS_LABELS[topStatus] || String(topStatus || 'Pending');
  const statusVariant = STATUS_VARIANTS[topStatus] || 'neutral';

  const source = application?.sourcePayload || {};
  const project = source.Project || {};
  const projectType = project.ProjectType || {};
  const program = project.Program || {};
  const funding = source.Funding || {};
  const fiscalInformation = source.FiscalInformation || {};
  const topTitle = firstDefined(project.Name, application?.projectName, application?.title, application?.applicationNumber);
  const legalName = firstDefined(source.LegalName, application?.legalName, `${application?.firstName || ''} ${application?.lastName || ''}`.trim());
  const fiscalYear = firstDefined(source.FiscalYear, application?.year);
  const applicationType = firstDefined(projectType.Description, source.ProjectType?.Description, application?.projectType);
  const dateReceived = firstDefined(source.DateReceived, application?.submittedAt, application?.createdAt);
  const federalShare = firstDefined(fiscalInformation.FederalShareRequested, funding.FederalAmount, application?.fundingAmount);
  const matchShare = firstDefined(fiscalInformation.MatchShareSupplied, funding.ApplicantAmount);

  const latestPrescreening = application?.latestPrescreening || null;
  const latestAIAnalysis = application?.latestAIAnalysis || null;
  const hasSavedAIAnalysis = Boolean(latestAIAnalysis || application?.aiAnalysisRunsMeta?.length);
  const attachments = useMemo(() => application?.attachments || [], [application?.attachments]);
  const formalReviews = application?.formalReviews || [];
  const attachmentLookup = useMemo(() => {
    const lookup = new Map();

    attachments.forEach((attachment) => {
      [attachment.filename, attachment.originalName, attachment.sourceLabel].forEach((name) => {
        const normalizedName = normalizeDocumentLookupName(name);
        if (normalizedName && !lookup.has(normalizedName)) {
          lookup.set(normalizedName, attachment);
        }
      });
    });

    return lookup;
  }, [attachments]);

  const toggleRiskSection = useCallback((sectionKey) => {
    setOpenRiskSections((current) => ({
      ...current,
      [sectionKey]: !(current[sectionKey] ?? true),
    }));
  }, []);
  const activityLog = application?.activityLog || [];
  const riskSummary = riskDashboard?.summary || {
    score: 0,
    level: 'LOW',
    alertsCount: 0,
    highAlerts: 0,
    courtCount: 0,
    newsCount: 0,
    isDemoData: false,
    prescreeningRequired: false,
  };
  const organizationRisk = riskDashboard?.organization || null;
  const selectedRiskPerson = useMemo(
    () => (riskDashboard?.people || []).find((item) => item.contact?.id === selectedRiskPersonId) || riskDashboard?.people?.[0] || null,
    [riskDashboard, selectedRiskPersonId]
  );

  const prescreeningCounts = (latestPrescreening?.checks || []).reduce(
    (totals, check) => {
      if (check.status === 'Pass') totals.pass += 1;
      else if (check.status === 'Warning') totals.warning += 1;
      else if (check.status === 'Fail') totals.fail += 1;
      return totals;
    },
    { pass: 0, warning: 0, fail: 0 }
  );
  const visiblePrescreeningFindings = (latestPrescreening?.reviewerSummary?.majorFindings || []).filter(
    (finding) => !PRESCREEN_SUMMARY_HIDDEN_PREFIXES.some((prefix) => String(finding || '').startsWith(prefix))
  );

  const reviewAverageScore = formalReviews.length
    ? (
      formalReviews
        .filter((review) => review.score != null && review.score !== '')
        .reduce((sum, review) => sum + Number(review.score || 0), 0) /
      Math.max(1, formalReviews.filter((review) => review.score != null && review.score !== '').length)
    )
    : 0;

  const reviewCriteriaMaxScore = useMemo(
    () => (application?.scoringCriteria || []).reduce((sum, criterion) => sum + Number(criterion.maxScore || 0), 0),
    [application?.scoringCriteria]
  );

  const formalReviewTotalScore = useMemo(
    () => (formalReviewForm.criterionReviews || []).reduce((sum, criterion) => sum + Number(criterion.awardedScore || 0), 0),
    [formalReviewForm.criterionReviews]
  );

  const aiCriterionScoresByTitle = useMemo(
    () => new Map((latestAIAnalysis?.criterionScores || []).map((criterion) => [criterion.title, criterion])),
    [latestAIAnalysis]
  );

  const handleToggleSection = (sectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const handleFormalReviewChange = (event) => {
    const { name, value } = event.target;
    setFormalReviewForm((current) => ({ ...current, [name]: value }));
  };

  const handleCriterionReviewChange = (index, field, value) => {
    setFormalReviewForm((current) => ({
      ...current,
      criterionReviews: (current.criterionReviews || []).map((criterion, criterionIndex) => (
        criterionIndex === index
          ? {
              ...criterion,
              [field]: value,
            }
          : criterion
      )),
    }));
  };

  const applyUpdatedApplication = (nextApplication, nextFeedback = '') => {
    setApplication(nextApplication);
    if (nextApplication.latestPrescreening?.checks?.[0]?.checkCode) {
      setExpandedCheck((current) => current || nextApplication.latestPrescreening.checks[0].checkCode);
    }
    if (nextFeedback) {
      setFeedback(nextFeedback);
      window.setTimeout(() => setFeedback(''), 2600);
    }
  };

  const runJsonAction = async (url, options, successMessage, busyKey) => {
    setBusyAction(busyKey);
    setError('');

    try {
      const response = await fetch(url, options);
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Request failed.');
      }

      applyUpdatedApplication(body, successMessage);
    } catch (runError) {
      setError(runError.message || 'Request failed.');
    } finally {
      setBusyAction('');
    }
  };

  const handleRunPrescreening = async () => {
    await runJsonAction(
      `${API_BASE_URL}/api/applications/${id}/prescreening/run`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      'AI prescreening completed.',
      'prescreening'
    );
  };

  const runRiskCheckRequest = async (kind) => {
    const isPerson = kind === 'person';
    const endpoint = isPerson
      ? `${API_BASE_URL}/api/risk/grants/${id}/person-check`
      : `${API_BASE_URL}/api/risk/grants/${id}/business-check`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(isPerson ? { contactId: selectedRiskPerson?.contact?.id || '' } : {}),
    });
    const body = await readApiPayload(response);
    if (!response.ok) {
      throw new Error(body?.message || `Unable to run ${isPerson ? 'person' : 'business'} risk check.`);
    }
  };

  const runRiskCheck = async () => {
    setBusyAction('risk-assessment');
    setRiskError('');

    try {
      await runRiskCheckRequest('business');
      if (selectedRiskPerson) {
        await runRiskCheckRequest('person');
      }

      await Promise.all([
        loadRiskDashboard(id, { quiet: true }),
        loadApplicationWorkspace(id, { quiet: true }),
      ]);
      setFeedback('AI risk assessment completed.');
      window.setTimeout(() => setFeedback(''), 2600);
    } catch (runError) {
      const fallbackDashboard = buildLocalRiskDashboardFallback(application);
      setRiskDashboard(fallbackDashboard);
      setSelectedRiskPersonId(fallbackDashboard.people?.[0]?.contact?.id || '');
      setRiskError('');
      setFeedback('Loaded temporary reference risk data.');
      window.setTimeout(() => setFeedback(''), 2600);
    } finally {
      setBusyAction('');
    }
  };

  const handleRunAIAnalysis = async ({ showProgressNotice = false } = {}) => {
    if (showProgressNotice) {
      setShowAIAnalysisNotice(true);
    }

    await runJsonAction(
      `${API_BASE_URL}/api/applications/${id}/ai-analysis/run`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      'AI analysis completed.',
      'analysis'
    );

    if (showProgressNotice) {
      setShowAIAnalysisNotice(false);
    }
  };

  const handleWorkspaceTabClick = (tabId) => {
    setActiveTab(tabId);

    if (tabId === 'review' && busyAction !== 'analysis' && !hasSavedAIAnalysis) {
      handleRunAIAnalysis({ showProgressNotice: true });
    }
  };

  const handleSaveFormalReview = async (event) => {
    event.preventDefault();
    await runJsonAction(
      `${API_BASE_URL}/api/applications/${id}/formal-review`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          score: formalReviewTotalScore,
          recommendation: formalReviewForm.recommendation,
          status: formalReviewForm.status,
          comments: formalReviewForm.comments,
          criterionReviews: (formalReviewForm.criterionReviews || []).map((criterion) => ({
            title: criterion.title,
            description: criterion.description,
            maxScore: criterion.maxScore,
            awardedScore: criterion.awardedScore === '' ? 0 : Number(criterion.awardedScore),
            notes: criterion.notes,
          })),
        }),
      },
      'Formal review saved.',
      'formal-review'
    );
  };

  const handleAttachmentUpload = async (event) => {
    event.preventDefault();
    if (selectedFiles.length === 0) {
      setError('Choose one or more files before uploading.');
      return;
    }

    setBusyAction('attachments');
    setError('');

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('attachments', file));

      const response = await fetch(`${API_BASE_URL}/api/applications/${id}/attachments/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Unable to upload attachments.');
      }

      setSelectedFiles([]);
      applyUpdatedApplication(body, 'Attachments uploaded.');
    } catch (uploadError) {
      setError(uploadError.message || 'Unable to upload attachments.');
    } finally {
      setBusyAction('');
    }
  };

  const handleAttachmentDelete = async (attachmentId, filename) => {
    if (!window.confirm(`Remove ${filename}?`)) return;

    await runJsonAction(
      `${API_BASE_URL}/api/applications/${id}/attachments/${attachmentId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      },
      'Attachment removed.',
      'delete-attachment'
    );
  };

  const handleAttachmentDownload = async (attachment) => {
    setBusyAction(`download-${attachment.attachmentId}`);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${id}/attachments/${attachment.attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Unable to download attachment.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.filename;
      link.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      setError(downloadError.message || 'Unable to download attachment.');
    } finally {
      setBusyAction('');
    }
  };

  const handleAttachmentPreview = async (attachment) => {
    setBusyAction(`preview-${attachment.attachmentId}`);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${id}/attachments/${attachment.attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Unable to open attachment preview.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      setPreviewAttachment(attachment);
    } catch (previewError) {
      setError(previewError.message || 'Unable to open attachment preview.');
    } finally {
      setBusyAction('');
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setPreviewAttachment(null);
  };

  const handlePrescreenSourcePreview = async (sourceName) => {
    const attachment = attachmentLookup.get(normalizeDocumentLookupName(sourceName));
    if (!attachment?.attachmentId) return;
    await handleAttachmentPreview(attachment);
  };

  const renderDetailsTab = () => (
    <div className="workspace-tab-stack">
      <DetailSection
        title="General Information"
        sectionKey="general"
        openSections={openSections}
        onToggle={handleToggleSection}
        defaultOpen
      >
        <div className="record-stack">
          <div className="record-grid">
            <DetailField label="Program" value={firstDefined(program.Description, application.grantProgram, application.programCode)} />
            <DetailField label="Fiscal Year" value={fiscalYear} />
            <DetailField label="Project Type" value={applicationType} />
          </div>
          <div className="record-group-card">
            <div className="record-group-title">General Information</div>
            <div className="record-grid">
              <DetailField label="Application Number" value={application.applicationNumber} />
              <DetailField label="Date Received" value={formatDate(dateReceived)} />
            </div>
          </div>
          <div className="record-group-card">
            <div className="record-group-title">Applicant Information</div>
            <div className="record-grid">
              <DetailField label="Legal Name" value={legalName} wide />
              <DetailField label="Employer/Taxpayer ID" value={firstDefined(source.EmployerTaxPayerID, source.EmployerTaxpayerID)} />
              <DetailField label="UEI/DUNS" value={firstDefined(source.UEIDUNS, source.UEI, source.DUNS)} />
            </div>
          </div>
          <div className="record-group-card">
            <div className="record-group-title">Organization Unit</div>
            <div className="record-grid">
              <DetailField label="Department Name" value={source.DepartmentName} />
              <DetailField label="Division Name" value={source.DivisionName} />
            </div>
          </div>
        </div>
      </DetailSection>

      <DetailSection title="Address/Contact" sectionKey="contact" openSections={openSections} onToggle={handleToggleSection}>
        <div className="record-grid">
          <DetailField label="Prefix" value={source.Prefix} />
          <DetailField label="Title" value={source.Title} />
          <DetailField label="Suffix" value={source.Suffix} />
          <DetailField label="First Name" value={firstDefined(source.FirstName, application.firstName)} />
          <DetailField label="Middle Name" value={source.MiddleName} />
          <DetailField label="Last Name" value={firstDefined(source.LastName, application.lastName)} />
          <DetailField label="Email" value={firstDefined(source.Email, application.email)} />
          <DetailField label="Phone" value={firstDefined(source.PhoneNumber, application.phone)} />
          <DetailField label="Fax" value={source.Fax} />
          <DetailField label="Address 1" value={source.Address1} wide />
          <DetailField label="Address 2" value={source.Address2} wide />
          <DetailField label="City" value={firstDefined(source.City, application.city)} />
          <DetailField label="State" value={firstDefined(source.State?.Code, application.state)} />
          <DetailField label="ZIP" value={firstDefined(source.Zip, application.zip)} />
        </div>
      </DetailSection>

      <DetailSection title="Project Information" sectionKey="project" openSections={openSections} onToggle={handleToggleSection}>
        <div className="record-grid">
          <DetailField label="Project Name" value={topTitle} wide />
          <DetailField label="Applicant Type" value={project.ApplicantType} />
          <DetailField label="Congressional District" value={project.CongressionalDistrict} />
          <DetailField label="District Project" value={project.CongressionalDistrictProject} />
          <DetailField label="Project Start Date" value={formatDate(firstDefined(project.StartDate, application.projectStartDate))} />
          <DetailField label="Project End Date" value={formatDate(firstDefined(project.EndDate, application.projectEndDate))} />
          <DetailField label="Description" value={firstDefined(project.Description, application.projectDescription)} wide />
        </div>
      </DetailSection>

      <DetailSection title="Budget" sectionKey="budget" openSections={openSections} onToggle={handleToggleSection}>
        <div className="record-grid">
          <DetailField label="Federal Amount" value={formatMoney(funding.FederalAmount)} />
          <DetailField label="Applicant Amount" value={formatMoney(funding.ApplicantAmount)} />
          <DetailField label="State Amount" value={formatMoney(funding.StateAmount)} />
          <DetailField label="Local Amount" value={formatMoney(funding.LocalAmount)} />
          <DetailField label="Other Amount" value={formatMoney(funding.OtherAmount)} />
          <DetailField label="Total Amount" value={formatMoney(funding.TotalAmount)} />
          <DetailField label="Funded Amount" value={formatMoney(funding.FundedAmount)} />
          <DetailField label="Program Amount" value={formatMoney(funding.ProgramAmount)} />
          <DetailField label="Applicant Delinquent" value={formatBoolean(funding.IsApplicantDelinquent)} />
          <DetailField label="Delinquency Explanation" value={funding.ExplainationDelinquent} wide />
        </div>
      </DetailSection>
    </div>
  );

  const renderPrescreeningTab = () => (
    <div className="workspace-tab-stack">
          <section className="dashboard-section workspace-prescreen-header-section">
            <div className="section-top">
              <div>
                <h2>AI Prescreening for Eligibility and Compliance</h2>
                <p className="table-meta">
                  Run intake-level checks against the selected NOFO, saved attachments, and the structured application record.
                </p>
          </div>
          <div className="section-actions">
            {application.prescreeningRunsMeta?.length > 0 && (
              <span className="table-meta">{application.prescreeningRunsMeta.length} saved run(s)</span>
            )}
            <button
              type="button"
              className="submit-btn"
              onClick={handleRunPrescreening}
              disabled={busyAction === 'prescreening'}
            >
              {busyAction === 'prescreening' ? 'Running...' : 'Run Prescreening'}
            </button>
          </div>
        </div>

      </section>

      {!latestPrescreening ? (
        <EmptyState
          title="No prescreening run yet"
          body="Run the AI prescreening step to evaluate the application against the NOFO requirements defined for its program."
        />
      ) : (
        <>
          <section className="dashboard-section workspace-prescreen-summary-section">
            <div className="workspace-prescreen-summary-card">
              <div className="workspace-prescreen-summary-top">
                <div className="workspace-prescreen-summary-copy">
                  <h2>Prescreening Summary</h2>
                </div>
                <StatusChip status={latestPrescreening.overallStatus} />
              </div>

              <div className="workspace-prescreen-summary-metrics">
                <div className="workspace-prescreen-summary-metric workspace-prescreen-summary-metric--pass">
                  <span className="workspace-prescreen-summary-metric-line">
                    <span className="record-field-label">Pass</span>
                    <strong>{prescreeningCounts.pass}</strong>
                  </span>
                </div>
                <div className="workspace-prescreen-summary-metric workspace-prescreen-summary-metric--warning">
                  <span className="workspace-prescreen-summary-metric-line">
                    <span className="record-field-label">Warning</span>
                    <strong>{prescreeningCounts.warning}</strong>
                  </span>
                </div>
                <div className="workspace-prescreen-summary-metric workspace-prescreen-summary-metric--fail">
                  <span className="workspace-prescreen-summary-metric-line">
                    <span className="record-field-label">Fail</span>
                    <strong>{prescreeningCounts.fail}</strong>
                  </span>
                </div>
                <div className="workspace-prescreen-summary-metric workspace-prescreen-summary-metric--total">
                  <span className="workspace-prescreen-summary-metric-line">
                    <span className="record-field-label">Checks</span>
                    <strong>{latestPrescreening.checks?.length || 0}</strong>
                  </span>
                </div>
              </div>

              {visiblePrescreeningFindings.length > 0 ? (
                <div className="workspace-prescreen-findings-inline">
                  {visiblePrescreeningFindings.slice(0, 4).map((finding) => (
                    <span key={finding}>{finding}</span>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-top">
              <h2>Check Results</h2>
              <span className="table-meta">{latestPrescreening.checks?.length || 0} total checks</span>
            </div>
            <div className="prescreen-check-list">
              {(latestPrescreening.checks || []).map((check) => {
                const isOpen = expandedCheck === check.checkCode;
                return (
                  <article key={check.checkCode} className={`prescreen-check-card ${String(check.status || '').toLowerCase()}`}>
                    <button
                      type="button"
                      className="prescreen-check-toggle"
                      onClick={() => setExpandedCheck(isOpen ? '' : check.checkCode)}
                    >
                      <div className="prescreen-check-main">
                        <div className="prescreen-check-heading">
                          <strong>{check.displayLabel || formatCheckLabel(check.checkCode)}</strong>
                          <span className="table-meta">{check.tier}</span>
                        </div>
                        <p>{check.message}</p>
                      </div>
                      <div className="prescreen-check-meta">
                        <span className="prescreen-confidence">Confidence {formatConfidence(check.confidence)}</span>
                        <StatusChip status={check.status} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="prescreen-check-body">
                        <div>
                          <span className="record-field-label">Evidence</span>
                          <div className="prescreen-check-evidence">
                            {(check.evidence?.length ? check.evidence : ['No evidence snippet captured']).map((item) => (
                              <span key={`${check.checkCode}-${item}`}>{item}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="record-field-label">Source Documents</span>
                          <div className="prescreen-check-evidence">
                            {(check.sourceDocumentsUsed?.length ? check.sourceDocumentsUsed : ['No document citation captured']).map((item) => {
                              const matchingAttachment = attachmentLookup.get(normalizeDocumentLookupName(item));

                              return matchingAttachment ? (
                                <button
                                  key={`${check.checkCode}-source-${item}`}
                                  type="button"
                                  className="prescreen-evidence-button"
                                  onClick={() => handlePrescreenSourcePreview(item)}
                                  disabled={busyAction === `preview-${matchingAttachment.attachmentId}`}
                                  title={`Preview ${item}`}
                                >
                                  {busyAction === `preview-${matchingAttachment.attachmentId}` ? 'Opening...' : item}
                                </button>
                              ) : (
                                <span key={`${check.checkCode}-source-${item}`}>{item}</span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );

  const renderRiskTab = () => (
    <div className="workspace-tab-stack">
      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>AI Risk Assessment</h2>
            <p className="table-meta">
              Run an AI-powered risk assessment against the applicant entity and related contacts using the external risk service.
            </p>
          </div>
          <div className="section-actions">
            {riskSummary.isDemoData ? <span className="table-meta">Demo data</span> : null}
            <StatusChip status={riskSummary.level || 'LOW'} />
            <button
              type="button"
              className="submit-btn"
              onClick={() => runRiskCheck()}
              disabled={busyAction === 'risk-assessment' || riskSummary.prescreeningRequired}
            >
              {busyAction === 'risk-assessment' ? 'Running...' : 'Run AI Risk Assessment Score'}
            </button>
          </div>
        </div>

        {riskError ? (
          <div className="workspace-inline-error">
            <strong>Risk data unavailable.</strong>
            <p>{riskError}</p>
          </div>
        ) : null}

        {riskSummary.prescreeningRequired ? (
          <div className="workspace-inline-warning">
            <strong>Run pre-screening for this grant before using AI Risk Assessment.</strong>
            <p>Risk checks are blocked until the grant has a prescreening result or an existing cached risk result.</p>
          </div>
        ) : null}

        {riskLoading && !riskDashboard ? (
          <EmptyState
            title="Loading risk signals"
            body="Pulling any cached business and person risk results for this grant."
          />
        ) : null}

        <div className="workspace-risk-report">
          <div className="workspace-risk-top">
            <div className="workspace-risk-score-card">
              <strong>{riskSummary.score}</strong>
              <span>/ 100</span>
              <RiskSeverityBadge level={riskSummary.level || 'LOW'} />
              <div className="workspace-risk-score-track">
                <span style={{ width: `${Math.max(6, Math.min(100, riskSummary.score || 0))}%` }} />
              </div>
              <p>OSINT: {organizationRisk?.breakdown?.osintScore || 0} / 100</p>
            </div>

            <div className="workspace-risk-summary-copy">
              <h3>AI Risk Summary</h3>
              <p>{organizationRisk?.aiSummary || 'Run the business check to generate the summarized risk narrative.'}</p>
            </div>
          </div>

          <div className="workspace-metrics-grid workspace-risk-summary-cards">
            <div className="workspace-metric-card">
              <span className="record-field-label">Risk score</span>
              <strong>{riskSummary.score} / 100</strong>
              <RiskSeverityBadge level={riskSummary.level || 'LOW'} />
            </div>
            <div className="workspace-metric-card">
              <span className="record-field-label">High alerts</span>
              <strong>{riskSummary.highAlerts} of {riskSummary.alertsCount}</strong>
            </div>
            <div className="workspace-metric-card">
              <span className="record-field-label">Court cases</span>
              <strong>{riskSummary.courtCount}</strong>
            </div>
            <div className="workspace-metric-card">
              <span className="record-field-label">News mentions</span>
              <strong>{riskSummary.newsCount}</strong>
            </div>
          </div>

          <RiskSection
            title="Fraud Risk Alerts"
            sectionKey="fraudAlerts"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            {(organizationRisk?.alerts || []).length > 0 ? (
              <div className="workspace-risk-table-wrap">
                <table className="workspace-risk-table">
                  <thead>
                    <tr>
                      <th>Score</th>
                      <th>Level</th>
                      <th>Alert ID</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizationRisk.alerts.map((alert) => (
                      <tr key={alert.id || alert.title}>
                        <td>—</td>
                        <td><RiskSeverityBadge level={alert.severity || 'LOW'} /></td>
                        <td>{alert.id}</td>
                        <td>{alert.title}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="table-meta">No fraud alerts returned yet.</p>}
          </RiskSection>

          <RiskSection
            title="Business Identity"
            sectionKey="businessIdentity"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-identity-grid">
              <div className="workspace-risk-identity-column">
                <DetailField label="Name" value={organizationRisk?.businessIdentity?.name} />
                <DetailField label="EIN" value={organizationRisk?.businessIdentity?.ein} />
                <DetailField label="UEI" value={organizationRisk?.businessIdentity?.uei || riskDashboard?.grantContext?.uei} />
                <DetailField label="Address" value={organizationRisk?.businessIdentity?.address} />
                <DetailField label="Incorporated" value={organizationRisk?.businessIdentity?.incorporated} />
              </div>
              <div className="workspace-risk-identity-column">
                <DetailField label="Years in business" value={organizationRisk?.businessIdentity?.yearsInBusiness} />
                <DetailField label="Industry" value={organizationRisk?.businessIdentity?.industry} />
                <DetailField label="Website" value={organizationRisk?.businessIdentity?.website} />
                <DetailField label="Status" value={organizationRisk?.businessIdentity?.status} />
              </div>
            </div>
          </RiskSection>

          <RiskSection
            title={`Financial Red Flags (${(organizationRisk?.financialRedFlags || []).length})`}
            sectionKey="financialRedFlags"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-flag-list">
              {(organizationRisk?.financialRedFlags || []).map((item, index) => (
                <article key={`flag-${index}`} className={`workspace-risk-flag-card ${item.severity || 'low'}`}>
                  <div className="workspace-risk-flag-main">
                    <strong>
                      {item.type}
                      {item.amount ? ` · ${item.amount}` : ''}
                      {item.filed ? ` · ${item.filed}` : ''}
                    </strong>
                    <p>{item.detail}</p>
                  </div>
                </article>
              ))}
            </div>
          </RiskSection>

          <RiskSection
            title={`Court Records (${(organizationRisk?.courtCases || []).length})`}
            sectionKey="courtRecords"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-table-wrap">
              <table className="workspace-risk-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Case #</th>
                    <th>Case Name</th>
                    <th>Court</th>
                    <th>Role</th>
                    <th>Type</th>
                    <th>Filed</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(organizationRisk?.courtCases || []).map((item, index) => (
                    <tr key={`case-${index}`}>
                      <td><span className="workspace-risk-tag dark">{item.source}</span></td>
                      <td>{item.caseNumber}</td>
                      <td>{item.caseName}</td>
                      <td>{item.court}</td>
                      <td>{item.role}</td>
                      <td className={String(item.type || '').toLowerCase() === 'criminal' ? 'workspace-risk-text-danger' : ''}>{item.type}</td>
                      <td>{item.filed}</td>
                      <td><span className={`workspace-risk-tag ${String(item.status || '').toLowerCase() === 'active' ? 'success' : 'dark'}`}>{item.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RiskSection>

          <RiskSection
            title={`Negative News (${(organizationRisk?.negativeNews || []).length})`}
            sectionKey="negativeNews"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-news-list">
              {(organizationRisk?.negativeNews || []).map((item, index) => (
                <article key={`news-${index}`} className="workspace-risk-news-card">
                  <strong>{item.title}</strong>
                  <p className="table-meta">{item.source} · {item.date}{item.match ? ` · Match: ${item.match}` : ''}</p>
                  <p>{item.summary || item.description || item.detail}</p>
                  {item.tag ? <span className="workspace-risk-tag neutral">{item.tag}</span> : null}
                </article>
              ))}
            </div>
          </RiskSection>

          <RiskSection
            title="Related Persons & Businesses"
            sectionKey="relatedEntities"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-dual-grid">
              <div>
                <span className="record-field-label">Persons</span>
                <div className="workspace-risk-chip-cloud">
                  {(organizationRisk?.relatedPeople || []).map((item, index) => (
                    <span key={`person-chip-${index}`} className="workspace-risk-chip">
                      {item.fullName || item.name}{item.role ? ` ${item.role}` : ''}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="record-field-label">Businesses</span>
                <div className="workspace-risk-chip-cloud">
                  {(organizationRisk?.relatedBusinesses || []).map((item, index) => (
                    <span key={`biz-chip-${index}`} className="workspace-risk-chip">
                      {item.businessName || item.name}{item.taxId ? ` EIN ${item.taxId}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </RiskSection>

          <RiskSection
            title={`Other Names / DBAs (${(organizationRisk?.otherNames || []).length})`}
            sectionKey="otherNames"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-chip-cloud">
              {(organizationRisk?.otherNames || []).map((item) => (
                <span key={item} className="workspace-risk-chip">{item}</span>
              ))}
            </div>
          </RiskSection>

          <RiskSection
            title={`Incorporation Records (${(organizationRisk?.incorporationRecords || []).length})`}
            sectionKey="incorporationRecords"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-table-wrap">
              <table className="workspace-risk-table">
                <thead>
                  <tr>
                    <th>Filing #</th>
                    <th>Type</th>
                    <th>State</th>
                    <th>Foreign State</th>
                    <th>Status</th>
                    <th>Filed</th>
                    <th>Registered Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {(organizationRisk?.incorporationRecords || []).map((item, index) => (
                    <tr key={`inc-${index}`}>
                      <td>{item.filingNumber}</td>
                      <td>{item.type}</td>
                      <td>{item.state}</td>
                      <td>{item.foreignState}</td>
                      <td><span className={`workspace-risk-tag ${String(item.status || '').toLowerCase() === 'active' ? 'success' : 'dark'}`}>{item.status}</span></td>
                      <td>{item.filed}</td>
                      <td>{item.registeredAgent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RiskSection>

          <RiskSection
            title="OSINT Intelligence"
            sectionKey="osintIntelligence"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-dual-grid">
              <div>
                <span className="record-field-label">Related People (OSINT)</span>
                <div className="workspace-risk-osint-list">
                  {(organizationRisk?.osintPeople || []).map((item, index) => (
                    <article key={`osint-person-${index}`} className="workspace-risk-osint-card">
                      <strong>{item.fullName}</strong>
                      <p>{item.role} · Source: {item.source}</p>
                      <p>Earliest record: {item.earliestRecord}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div>
                <span className="record-field-label">Related Businesses (OSINT)</span>
                <div className="workspace-risk-osint-list">
                  {(organizationRisk?.osintBusinesses || []).map((item, index) => (
                    <article key={`osint-business-${index}`} className="workspace-risk-osint-card">
                      <strong>{item.businessName}</strong>
                      <p>{item.relationship} · Source: {item.source}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </RiskSection>

          <RiskSection
            title="Revenue Trend"
            sectionKey="revenueTrend"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-table-wrap workspace-risk-table-wrap-narrow">
              <table className="workspace-risk-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Annual Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(organizationRisk?.revenueTrend || []).map((item, index) => (
                    <tr key={`revenue-${index}`}>
                      <td>{item.year}</td>
                      <td>{item.annualRevenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RiskSection>

          <RiskSection
            title="Corporate Activity"
            sectionKey="corporateActivity"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-activity-list">
              {(organizationRisk?.corporateActivity || []).map((item, index) => (
                <article key={`corp-${index}`} className="workspace-risk-activity-row">
                  <strong>{item.type} — {item.title}</strong>
                  <p>{item.date}</p>
                </article>
              ))}
            </div>
          </RiskSection>

          <RiskSection
            title="Relationship Mapping"
            sectionKey="relationshipMapping"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-risk-chip-cloud">
              {(organizationRisk?.relationshipMapping?.relatedEntities || []).map((item) => (
                <span key={item} className="workspace-risk-chip">{item}</span>
              ))}
            </div>
          </RiskSection>
        </div>

        <div className="workspace-two-column">
          <RiskSection
            title="Activity Timeline"
            sectionKey="activityTimeline"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-panel-top">
              <h3>Activity Timeline</h3>
              <p>Saved risk activity and explanatory notes captured from the provider response.</p>
            </div>
            {(organizationRisk?.activityTimeline || []).length > 0 ? (
              <div className="workspace-activity-log">
                {organizationRisk.activityTimeline.map((item, index) => (
                  <div key={`risk-activity-${index}`} className="workspace-activity-item">
                    <span className="workspace-activity-marker" />
                    <div className="workspace-activity-content">
                      <div className="workspace-activity-top">
                        <strong>{item.title}</strong>
                        <span className="table-meta">{formatDateTime(item.timestamp)}</span>
                      </div>
                      <p>{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="table-meta">No risk activity timeline is available yet.</p>
            )}
          </RiskSection>

          <RiskSection
            title="Data Sources Used"
            sectionKey="dataSources"
            openSections={openRiskSections}
            onToggle={toggleRiskSection}
          >
            <div className="workspace-panel-top">
              <h3>Data Sources Used</h3>
              <p>Sources that contributed to the risk result or indicate demo-mode fallback.</p>
            </div>
            {(organizationRisk?.dataSourcesUsed || []).length > 0 ? (
              <div className="prescreen-check-evidence">
                {organizationRisk.dataSourcesUsed.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            ) : (
              <p className="table-meta">No data source labels are available.</p>
            )}
          </RiskSection>
        </div>
      </section>
    </div>
  );

  const renderReviewTab = () => (
    <div className="workspace-tab-stack">
      <section className="dashboard-section">
        <div className="section-actions workspace-review-actions">
          <div className="section-actions">
            {application.aiAnalysisRunsMeta?.length > 0 && (
              <span className="table-meta">{application.aiAnalysisRunsMeta.length} saved AI run(s)</span>
            )}
            <button
              type="button"
              className="submit-btn"
              onClick={handleRunAIAnalysis}
              disabled={busyAction === 'analysis'}
            >
              {busyAction === 'analysis' ? 'Running AI Review...' : 'Run AI Review'}
            </button>
          </div>
        </div>

        <div className="workspace-review-layout">
          <form className="workspace-panel workspace-review-form-panel" onSubmit={handleSaveFormalReview}>
            <div className="workspace-panel-top">
              <h3>Reviewer Notes</h3>
              <p>Review the AI scoring and add your criterion-specific notes before completing the worksheet below.</p>
            </div>

            <div className="workspace-review-criteria-header">
              <div>
                <h3>Criteria Scoring Worksheet</h3>
                <p>Each criterion shows the AI reviewer’s score, confidence, and reasoning alongside your own scoring inputs.</p>
              </div>
            </div>

            {(formalReviewForm.criterionReviews || []).length > 0 ? (
              <div className="workspace-review-criteria-list">
                {formalReviewForm.criterionReviews.map((criterion, index) => (
                  <div key={criterion.title || index} className="workspace-review-criterion-card">
                    <div className="workspace-review-criterion-top">
                      <div className="workspace-review-criterion-copy">
                        <strong>{criterion.title}</strong>
                        {criterion.description ? (
                          <p className="workspace-review-criterion-description">{criterion.description}</p>
                        ) : null}
                      </div>
                      <div className="workspace-review-criterion-score">
                        <label className="form-field">
                          <span>Reviewer Score</span>
                          <input
                            className="search-input"
                            type="number"
                            min="0"
                            max={criterion.maxScore || 0}
                            value={criterion.awardedScore}
                            onChange={(event) => handleCriterionReviewChange(index, 'awardedScore', event.target.value)}
                          />
                        </label>
                        <span className="workspace-review-score-cap">Max <strong>{criterion.maxScore || 0}</strong></span>
                      </div>
                    </div>
                    {aiCriterionScoresByTitle.get(criterion.title) ? (
                      <div className="workspace-review-ai-insight">
                        <div className="workspace-review-ai-insight-top">
                          <strong>AI Reviewer</strong>
                          <span className="workspace-review-ai-score-line">
                            <strong>
                              AI Score {aiCriterionScoresByTitle.get(criterion.title).awardedScore ?? 0} / {aiCriterionScoresByTitle.get(criterion.title).maxScore ?? criterion.maxScore ?? 0}
                            </strong>
                            <span>Confidence {formatConfidence(aiCriterionScoresByTitle.get(criterion.title).confidence)}</span>
                          </span>
                        </div>
                        {aiCriterionScoresByTitle.get(criterion.title).reasoning ? (
                          <p>{aiCriterionScoresByTitle.get(criterion.title).reasoning}</p>
                        ) : null}
                        <div className="workspace-review-ai-points">
                          <p><strong>Strengths:</strong> {formatNarrativePoints(aiCriterionScoresByTitle.get(criterion.title).strengths)}</p>
                          <p><strong>Weaknesses:</strong> {formatNarrativePoints(aiCriterionScoresByTitle.get(criterion.title).weaknesses)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="workspace-review-ai-insight workspace-review-ai-insight-empty">
                        <span className="table-meta">No AI score is available for this criterion yet.</span>
                      </div>
                    )}
                    <label className="form-field">
                      <span>Reviewer Notes</span>
                      <textarea
                        className="search-input"
                        rows={2}
                        value={criterion.notes}
                        onChange={(event) => handleCriterionReviewChange(index, 'notes', event.target.value)}
                        placeholder={`Add notes for ${criterion.title}`}
                      />
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No scoring criteria configured"
                body="Set program scoring criteria first so reviewers can score this application criterion by criterion."
              />
            )}

            {latestAIAnalysis ? (
              <div className="workspace-review-ai-summary">
                <div className="workspace-panel-top">
                  <h3>AI Decision</h3>
                  <p>Use the AI reviewer summary as a final reference before completing your decision.</p>
                </div>
                <strong>{latestAIAnalysis.executiveSummary}</strong>
                {latestAIAnalysis.aiReviewerSummary ? (
                  <p>{latestAIAnalysis.aiReviewerSummary}</p>
                ) : null}
                {!latestAIAnalysis.aiUsed && getAIRunSourceDetail(latestAIAnalysis) ? (
                  <p className="table-meta">{getAIRunSourceDetail(latestAIAnalysis)}</p>
                ) : null}
              </div>
            ) : (
              <EmptyState
                title="No AI Decision yet"
                body="Run AI Review to load criterion-by-criterion scoring guidance before completing your review."
              />
            )}

            <div className="workspace-review-footer-panel">
              <div className="workspace-panel-top">
                <h3>Reviewer Decision</h3>
                <p>Complete your recommendation, review status, and overall summary after working through the notes above.</p>
              </div>
              <div className="workspace-review-meta-grid">
                <div className="workspace-review-total-card">
                  <span className="record-field-label">Total Score</span>
                  <strong>{formalReviewTotalScore} / {reviewCriteriaMaxScore}</strong>
                </div>
                <label className="form-field">
                  <span>Recommendation</span>
                  <select className="search-input" name="recommendation" value={formalReviewForm.recommendation} onChange={handleFormalReviewChange}>
                    {FORMAL_RECOMMENDATION_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="form-field">
                  <span>Review Status</span>
                  <select className="search-input" name="status" value={formalReviewForm.status} onChange={handleFormalReviewChange}>
                    {FORMAL_REVIEW_STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="form-field workspace-review-overall-notes">
                <span>Overall Review Notes</span>
                <textarea
                  className="search-input"
                  rows={4}
                  name="comments"
                  value={formalReviewForm.comments}
                  onChange={handleFormalReviewChange}
                  placeholder="Summarize your overall assessment, rationale, and next-step recommendation."
                />
              </label>

              <div className="section-actions">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={busyAction === 'formal-review' || (formalReviewForm.criterionReviews || []).length === 0}
                >
                  {busyAction === 'formal-review' ? 'Saving...' : 'Save Formal Review'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );

  const renderAllReviewsTab = () => (
    <div className="workspace-tab-stack">
      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>All Reviews</h2>
            <p className="table-meta">
              Compare reviewer scores, recommendations, completion status, and written observations.
            </p>
          </div>
          <span className="table-meta">{formalReviews.length} review(s)</span>
        </div>

        {formalReviews.length === 0 ? (
          <EmptyState
            title="No formal reviews yet"
            body="Once reviewers save their formal assessments, they will appear here for comparison."
          />
        ) : (
          <>
            <div className="workspace-metrics-grid workspace-metrics-grid-compact">
              <div className="workspace-metric-card">
                <span className="record-field-label">Average Score</span>
                <strong>{reviewAverageScore ? reviewAverageScore.toFixed(1) : '—'}</strong>
              </div>
              <div className="workspace-metric-card">
                <span className="record-field-label">Completed Reviews</span>
                <strong>{formalReviews.filter((review) => review.status === 'Completed').length}</strong>
              </div>
              <div className="workspace-metric-card">
                <span className="record-field-label">Distinct Reviewers</span>
                <strong>{formalReviews.length}</strong>
              </div>
            </div>

            <div className="grants-table-wrapper">
              <table className="grants-table">
                <thead>
                  <tr>
                    <th>Reviewer</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Recommendation</th>
                    <th>Updated</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {formalReviews.map((review) => (
                    <tr key={review.reviewId}>
                      <td>{review.reviewerEmail}</td>
                      <td><StatusChip status={review.status} /></td>
                      <td>{review.score ?? '—'}</td>
                      <td>{review.recommendation || '—'}</td>
                      <td>{formatDateTime(review.updatedAt) || '—'}</td>
                      <td>{review.comments || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="workspace-card-list">
              {formalReviews.map((review) => (
                <div key={`${review.reviewId}-detail`} className="workspace-review-card">
                  <div className="workspace-review-card-top">
                    <strong>{review.reviewerEmail}</strong>
                    <div className="workspace-findings-meta">
                      <span className="table-meta">{review.recommendation || 'No recommendation'}</span>
                      <StatusChip status={review.status} />
                    </div>
                  </div>
                  {(review.strengths || []).length > 0 && (
                    <div>
                      <span className="record-field-label">Strengths</span>
                      <ul className="prescreen-summary-list">
                        {review.strengths.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {(review.concerns || []).length > 0 && (
                    <div>
                      <span className="record-field-label">Concerns</span>
                      <ul className="prescreen-summary-list">
                        {review.concerns.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {(review.criterionReviews || []).length > 0 && (
                    <div>
                      <span className="record-field-label">Criterion Scores</span>
                      <div className="workspace-review-criteria-readonly">
                        {review.criterionReviews.map((criterion) => (
                          <div key={`${review.reviewId}-${criterion.title}`} className="workspace-review-criteria-readonly-item">
                            <div className="workspace-review-criteria-readonly-top">
                              <strong>{criterion.title}</strong>
                              <span>{criterion.awardedScore ?? 0} / {criterion.maxScore ?? 0}</span>
                            </div>
                            {criterion.notes ? <p>{criterion.notes}</p> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );

  const renderAttachmentsTab = () => (
    <div className="workspace-tab-stack">
      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>Attachments</h2>
            <p className="table-meta">
              Upload supporting files, including ZIP packages, and manage the application’s working document set.
            </p>
          </div>
          <span className="table-meta">{attachments.length} saved attachment(s)</span>
        </div>

        <form className="workspace-panel" onSubmit={handleAttachmentUpload}>
          <div className="workspace-panel-top">
            <h3>Upload Files</h3>
            <p>ZIP uploads are flattened into supported files and added to the application’s attachment list.</p>
          </div>
          <label className="form-field record-field-wide">
            <span>Files</span>
            <input type="file" multiple onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))} />
          </label>
          {selectedFiles.length > 0 && (
            <div className="prescreen-file-list">
              {selectedFiles.map((file) => (
                <span key={`${file.name}-${file.size}`}>{file.name}</span>
              ))}
            </div>
          )}
          <div className="section-actions">
            <button type="submit" className="submit-btn" disabled={busyAction === 'attachments'}>
              {busyAction === 'attachments' ? 'Uploading...' : 'Upload Attachments'}
            </button>
          </div>
        </form>

        {attachments.length === 0 ? (
          <EmptyState
            title="No attachments yet"
            body="Upload the application package here so prescreening and reviewer analysis can use the saved documents."
          />
        ) : (
          <div className="workspace-card-list">
            {attachments.map((attachment) => (
              <div key={attachment.attachmentId} className="workspace-attachment-card">
                <div>
                  <strong>{attachment.filename}</strong>
                  <p>
                    {attachment.categoryLabel || 'Unclassified'} · {attachment.mimeType || 'Unknown type'} · {attachment.size ? `${Math.round(attachment.size / 1024)} KB` : '—'}
                  </p>
                  <p>Uploaded {formatDateTime(attachment.uploadedAt) || '—'} by {attachment.uploadedBy || 'Unknown'}</p>
                </div>
                <div className="section-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => handleAttachmentPreview(attachment)}
                    disabled={!attachment.attachmentId || busyAction === `preview-${attachment.attachmentId}`}
                  >
                    {busyAction === `preview-${attachment.attachmentId}` ? 'Opening...' : 'Preview'}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => handleAttachmentDownload(attachment)}
                    disabled={busyAction === `download-${attachment.attachmentId}`}
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    className="submit-btn danger"
                    onClick={() => handleAttachmentDelete(attachment.attachmentId, attachment.filename)}
                    disabled={busyAction === 'delete-attachment'}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );

  const renderActivityTab = () => (
    <div className="workspace-tab-stack">
      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>Activity Log</h2>
            <p className="table-meta">
              Trace uploads, AI runs, review saves, and status changes in chronological order.
            </p>
          </div>
          <span className="table-meta">{activityLog.length} activity item(s)</span>
        </div>

        {activityLog.length === 0 ? (
          <EmptyState
            title="No activity yet"
            body="Actions taken on this application will appear here as the review workflow progresses."
          />
        ) : (
          <div className="workspace-activity-log">
            {activityLog.map((entry, index) => (
              <div key={`${entry.timestamp}-${index}`} className="workspace-activity-item">
                <div className="workspace-activity-marker" />
                <div className="workspace-activity-content">
                  <div className="workspace-activity-top">
                    <strong>{entry.message}</strong>
                    <span className="table-meta">{formatDateTime(entry.timestamp)}</span>
                  </div>
                  <p>{entry.actorEmail || 'system'} · {entry.action}</p>
                  {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                    <div className="prescreen-check-evidence">
                      {Object.entries(entry.metadata).map(([key, value]) => (
                        <span key={`${entry.action}-${key}`}>{key}: {Array.isArray(value) ? value.join(', ') : String(value)}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'details':
        return renderDetailsTab();
      case 'prescreening':
        return renderPrescreeningTab();
      case 'risk':
        return renderRiskTab();
      case 'analysis':
        return renderReviewTab();
      case 'review':
        return renderReviewTab();
      case 'all-reviews':
        return renderAllReviewsTab();
      case 'attachments':
        return renderAttachmentsTab();
      case 'activity':
        return renderActivityTab();
      default:
        return renderDetailsTab();
    }
  };

  if (loading) {
    return (
      <div className="page-stack">
        <section className="dashboard-section">
          <p className="no-results">Loading application review workspace...</p>
        </section>
      </div>
    );
  }

  if (loadError || !application) {
    return (
      <div className="page-stack">
        <section className="dashboard-section">
          <p className="section-error">{loadError || 'Application not found.'}</p>
          <div className="form-actions">
            <button className="cancel-btn" onClick={() => navigate('/dashboard/applications')}>
              Back to Applications
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <section className="detail-header-strip">
        <div className="detail-header-main">
          <h1>{topTitle || 'Application Detail'}</h1>
          <div className="detail-header-meta">
            <span>{fiscalYear || '—'}</span>
            <span>Project Type: {applicationType || '—'}</span>
          </div>
          <div className="detail-header-org">{legalName || '—'}</div>
        </div>
        <div className="detail-header-side">
          <div className="detail-header-topline">
            <span className="detail-grant-number">
              {application.applicationNumber || application._id || application.id}
            </span>
            <span className={`detail-status-pill ${statusVariant}`}>{statusText}</span>
          </div>
          <div className="detail-header-stats">
            <div>
              <span>Federal Share:</span>
              <strong>{formatMoney(federalShare) || '—'}</strong>
            </div>
            <div>
              <span>Match Share Supplied:</span>
              <strong>{formatMoney(matchShare) || '—'}</strong>
            </div>
            <div>
              <span>Start Date:</span>
              <strong>{formatDate(firstDefined(project.StartDate, application.projectStartDate)) || '—'}</strong>
            </div>
            <div>
              <span>End Date:</span>
              <strong>{formatDate(firstDefined(project.EndDate, application.projectEndDate)) || '—'}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-section application-workspace-tabs-section">
        <div className="application-workspace-tabs">
          {WORKSPACE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`application-workspace-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleWorkspaceTabClick(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {feedback && (
        <section className="dashboard-section">
          <p className="success-message">{feedback}</p>
        </section>
      )}
      {error && (
        <section className="dashboard-section">
          <p className="section-error">{error}</p>
        </section>
      )}

      {renderActiveTab()}

      {showAIAnalysisNotice && (
        <div className="analysis-progress-overlay" role="dialog" aria-modal="true" aria-labelledby="ai-analysis-progress-title">
          <div className="analysis-progress-modal">
            <div className="analysis-progress-spinner" aria-hidden="true" />
            <h3 id="ai-analysis-progress-title">AI Analysis In Progress, Please Wait...</h3>
            <p>The reviewer notes workspace is preparing the latest AI scoring and rationale for this application.</p>
          </div>
        </div>
      )}

      <div className="detail-actions-row">
        <button className="cancel-btn" onClick={() => navigate('/dashboard/applications')}>
          Back
        </button>
      </div>

      {previewAttachment && (
        <div
          className="document-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Preview ${previewAttachment.filename}`}
          onClick={handleClosePreview}
        >
          <div className="document-preview-modal" onClick={(event) => event.stopPropagation()}>
            <div className="document-preview-modal-top">
              <h2 className="document-preview-modal-title">{previewAttachment.filename}</h2>
              <button type="button" className="cancel-btn" onClick={handleClosePreview}>
                Close
              </button>
            </div>
            <iframe
              className="document-preview-frame"
              title={previewAttachment.filename}
              src={previewUrl}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetail;
